/* Why did the score change?
 *
 * The most important interaction on this screen, and it is built entirely out of two `Score`
 * payloads — the current one and the one behind the previous trend point. No generated prose, no
 * heuristic: `points_lost` is on the wire, `sum(points_lost)` is exactly `100 - value` by the
 * contract's own guarantee, and every figure below is that arithmetic differenced and ranked.
 *
 * **The first thing it establishes is whether this is a data change at all.** Two runs sharing an
 * execution plan hash provably measured the same rule versions against the same columns of the same
 * dataset version, so a difference between them is the data moving. Without that, part of the
 * difference is configuration — a rule bound, unbound or re-versioned — and ranking dimensions
 * before saying so would attribute somebody's edit to the customer's data.
 *
 * **Contributors are ranked by *weighted* movement, not by raw movement.** A ten-point fall in a
 * dimension weighted 0.02 moves the total by 0.2 and is not the story; a three-point fall weighted
 * 0.25 is. Ranking by the raw delta is the mistake that makes this section point at the wrong place.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { UnavailableState } from '@/shared/ui/States';

import { useScore } from '../api/scoring';
import type { Score } from '../api/scoring';
import { compareScores, movedRulesIn, primaryContributors } from '../model/compare';
import type { RunDiff } from '../model/compare';
import { formatScore } from '../model/presentation';
import type { DrawerTab } from '../model/types';

export interface ScoreChangeExplanationProps {
  readonly score: Score;
  /** The score behind the previous trend point. `undefined` when this dataset has been scored once. */
  readonly previousScoreId: string | undefined;
  readonly onGoTo: (tab: DrawerTab) => void;
}

/** The one fact that decides how everything below it should be read. */
function PlanVerdict({ diff }: { readonly diff: RunDiff }): ReactElement {
  const { t } = useTranslation();

  if (!diff.comparable) {
    return (
      <Alert tone="notice" title={t('scoring.change.modelChangedTitle')}>
        <p className="lq-nomargin">
          {t('scoring.change.modelChangedBody', {
            before: diff.modelBefore,
            after: diff.modelNow,
          })}
        </p>
      </Alert>
    );
  }
  return (
    <Alert
      tone={diff.samePlan ? 'notice' : 'warning'}
      title={
        diff.samePlan
          ? t('scoring.change.samePlanTitle')
          : t('scoring.change.differentPlanTitle')
      }
    >
      <p className="lq-nomargin">
        {diff.samePlan
          ? t('scoring.change.samePlanBody', { hash: diff.planNow })
          : t('scoring.change.differentPlanBody', {
              before: diff.planBefore,
              after: diff.planNow,
              added: diff.rulesAdded,
              removed: diff.rulesRemoved,
              reVersioned: diff.rulesReVersioned,
            })}
        {diff.datasetVersionChanged
          ? ` ${t('scoring.change.datasetVersionChanged')}`
          : ''}
      </p>
    </Alert>
  );
}

export function ScoreChangeExplanation({
  score,
  previousScoreId,
  onGoTo,
}: ScoreChangeExplanationProps): ReactElement {
  const { t } = useTranslation();
  const previous = useScore(previousScoreId);

  if (previousScoreId === undefined) {
    return (
      <EmptyState
        title={t('scoring.change.noComparison')}
        guidance={t('scoring.change.noComparisonGuidance')}
      />
    );
  }
  if (previous.isPending) return <SkeletonRows rows={4} columns={3} />;
  if (previous.isError) {
    return (
      <UnavailableState
        title={t('scoring.change.previousUnreadable')}
        guidance={previous.error.message}
      />
    );
  }

  const diff = compareScores(score, previous.data);
  const contributors = primaryContributors(diff);
  const worstMove = contributors.reduce(
    (most, move) => Math.max(most, Math.abs(move.weighted ?? 0)),
    0,
  );

  const headline =
    diff.overallDelta === null
      ? null
      : diff.overallDelta > 0
        ? 'rose'
        : diff.overallDelta < 0
          ? 'fell'
          : 'flat';

  return (
    <div className="lq-stack">
      {headline === null ? (
        <Alert tone="notice" title={t('scoring.change.notComparable')}>
          <p className="lq-nomargin">{t('scoring.change.notComparableBody')}</p>
        </Alert>
      ) : headline === 'flat' ? (
        <Alert tone="notice" title={t('scoring.change.noMovementTitle')}>
          <p className="lq-nomargin">{t('scoring.change.noMovementBody')}</p>
        </Alert>
      ) : (
        <Alert
          tone={headline === 'fell' ? 'error' : 'ok'}
          title={t(
            headline === 'fell' ? 'scoring.change.fellTitle' : 'scoring.change.roseTitle',
            {
              points: formatScore(Math.abs(diff.overallDelta ?? 0)),
            },
          )}
        >
          <p className="lq-nomargin">
            {t('scoring.change.fromTo', {
              before: diff.overallBefore === null ? '—' : formatScore(diff.overallBefore),
              after: diff.overallNow === null ? '—' : formatScore(diff.overallNow),
            })}
          </p>
        </Alert>
      )}

      <PlanVerdict diff={diff} />

      <section>
        <h3 className="lq-drawer__h">{t('scoring.change.contributorsHead')}</h3>
        {contributors.length === 0 ? (
          <p className="lq-muted lq-nomargin">{t('scoring.change.noContributors')}</p>
        ) : (
          <ol className="lq-why">
            {contributors.map((move, rank) => {
              const rules = movedRulesIn(diff, move.dimension);
              const share =
                worstMove === 0 ? 0 : (Math.abs(move.weighted ?? 0) / worstMove) * 100;
              const down = (move.delta ?? 0) < 0;
              return (
                <li className="lq-why__item" key={move.dimension}>
                  <span className="lq-why__rank" aria-hidden="true">
                    {rank + 1}
                  </span>
                  <div className="lq-why__body">
                    <p className="lq-why__head">
                      <span className="lq-why__name">
                        {t(`scoring.dimension.${move.dimension}`)}
                      </span>
                      <span
                        className={
                          down ? 'lq-delta lq-delta--down' : 'lq-delta lq-delta--up'
                        }
                      >
                        <span aria-hidden="true">{down ? '▼' : '▲'}</span>{' '}
                        {formatScore(Math.abs(move.delta ?? 0))}
                      </span>
                      <span className="lq-why__weight">
                        {t('scoring.change.weightedBy', {
                          weight: move.weight,
                          points: formatScore(Math.abs(move.weighted ?? 0)),
                        })}
                      </span>
                    </p>
                    <ul className="lq-why__facts">
                      {rules.slice(0, 2).map((rule) => (
                        <li key={rule.ruleName}>
                          {t('scoring.change.rulePass', {
                            rule: rule.ruleName,
                            before:
                              rule.passBefore === null
                                ? '—'
                                : (rule.passBefore * 100).toFixed(1),
                            after:
                              rule.passNow === null
                                ? '—'
                                : (rule.passNow * 100).toFixed(1),
                          })}
                          {rule.newFailures > 0 && (
                            <>
                              {' '}
                              {t('scoring.change.newFailures', {
                                count: rule.newFailures,
                              })}
                            </>
                          )}
                          {rule.labelChanged && (
                            <>
                              {' '}
                              {t('scoring.change.ruleReVersioned', {
                                before: rule.previousLabel ?? '—',
                                after: rule.ruleLabel,
                              })}
                            </>
                          )}
                        </li>
                      ))}
                      {rules.length === 0 && (
                        <li>
                          {t('scoring.change.noRuleMoved', {
                            records: move.affectedRecords,
                          })}
                        </li>
                      )}
                      <li>
                        {t('scoring.change.dimensionFacts', {
                          checks: move.failedChecks,
                          counted: move.rulesCounted,
                          records: move.affectedRecords,
                        })}
                      </li>
                    </ul>
                    <span className="lq-why__bar" aria-hidden="true">
                      <span
                        className={
                          down
                            ? 'lq-why__fill lq-why__fill--down'
                            : 'lq-why__fill lq-why__fill--up'
                        }
                        style={{ width: `${String(Math.round(share))}%` }}
                      />
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      <section>
        <h3 className="lq-drawer__h">{t('scoring.change.outcomesHead')}</h3>
        <dl className="lq-figures">
          <div>
            <dt>{t('scoring.change.newFailuresLabel')}</dt>
            <dd className="lq-delta--down">{diff.newFailures.toLocaleString()}</dd>
          </div>
          <div>
            <dt>{t('scoring.change.resolvedLabel')}</dt>
            <dd className="lq-delta--up">{diff.resolvedFailures.toLocaleString()}</dd>
          </div>
          <div>
            <dt>{t('scoring.change.coverageLabel')}</dt>
            <dd>
              {diff.coverageBefore === null || diff.coverageNow === null
                ? t('scoring.listing.noColumns')
                : t('scoring.change.coverageMoved', {
                    before: Math.round(diff.coverageBefore * 100),
                    after: Math.round(diff.coverageNow * 100),
                  })}
            </dd>
          </div>
        </dl>
      </section>

      <div className="lq-drawer__actions">
        <button
          type="button"
          className="lq-btn lq-btn--secondary lq-btn--compact"
          onClick={() => {
            onGoTo('runs');
          }}
        >
          {t('scoring.action.compareRuns')}
        </button>
        <button
          type="button"
          className="lq-btn lq-btn--secondary lq-btn--compact"
          onClick={() => {
            onGoTo('checks');
          }}
        >
          {t('scoring.action.viewRecords')}
        </button>
      </div>
    </div>
  );
}
